import { Component, type ReactNode } from 'react';
import { captureErrorBoundary } from '../../services/error-tracking';
import { Button } from '../ui/Button';
import { isChunkLoadError, reloadOnceForStaleChunk } from '../../lib/chunkReload';

export interface ErrorBoundaryConfig {
  /** Custom title for the error UI */
  title?: string;
  /** Custom description for the error UI */
  description?: string;
  /** Custom fallback render function */
  fallback?: (error: Error, reset: () => void) => ReactNode;
  /** Callback when error is caught */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  /** Callback when reset button is clicked */
  onReset?: () => void;
  /** Show reset button (default: true) */
  showResetButton?: boolean;
  /** Custom reset button label */
  resetButtonLabel?: string;
  /** Name for the error boundary (for debugging) */
  name?: string;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  config?: ErrorBoundaryConfig;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Stale tab after a deploy: reload once automatically (guarded against loops).
    if (isChunkLoadError(error)) {
      reloadOnceForStaleChunk();
    }
    const sectionName = this.props.config?.name;
    const logPrefix = sectionName ? `[${sectionName}] ErrorBoundary caught an error:` : 'ErrorBoundary caught an error:';
    console.error(logPrefix, error, errorInfo);
    // Send to error tracking service
    captureErrorBoundary(error, {
      componentStack: errorInfo.componentStack || undefined,
      sectionName,
    });

    // Call custom onError callback if provided
    this.props.config?.onError?.(error, errorInfo);
  }

  handleReset = () => {
    // Call custom onReset callback if provided
    this.props.config?.onReset?.();

    // Reset error state
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.config?.fallback) {
        return this.props.config.fallback(this.state.error!, this.handleReset);
      }

      // Default fallback UI
      const isChunkError = this.state.error?.message?.includes('dynamically imported module') ||
                          this.state.error?.message?.includes('Loading chunk') ||
                          this.state.error?.message?.includes('Failed to fetch');

      const title = this.props.config?.title || (isChunkError ? 'Update Available' : 'Something went wrong');
      const description = this.props.config?.description || (
        isChunkError
          ? 'A new version of the app is available. Please refresh to get the latest updates.'
          : (this.state.error?.message || 'An unexpected error occurred')
      );
      const showResetButton = this.props.config?.showResetButton ?? true;
      const resetButtonLabel = this.props.config?.resetButtonLabel || (isChunkError ? 'Refresh Page' : 'Try Again');

      return (
        <div className="min-h-screen bg-gradient-to-br from-forest-900 via-forest-800 to-forest-950 flex flex-col items-center justify-center p-4">
          <div className="w-16 h-16 rounded-2xl bg-red-500/20 flex items-center justify-center mb-4">
            <span className="text-3xl">{isChunkError ? '🔄' : '⚠️'}</span>
          </div>
          <h1 className="font-display text-2xl font-semibold text-white mb-2">
            {title}
          </h1>
          <p className="text-forest-300 mb-6 text-center max-w-md">
            {description}
          </p>
          {showResetButton && (
            <Button
              variant="primary"
              onClick={() => {
                if (isChunkError) {
                  // For chunk errors, do a full page reload to get fresh assets
                  window.location.reload();
                } else {
                  this.handleReset();
                }
              }}
            >
              {resetButtonLabel}
            </Button>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}
